import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useMutation } from '@tanstack/react-query';
import { useSnowflakeContext } from '../contexts/SnowflakeContext';
import {
  SnowflakeAuthMethod,
  PasswordCredentials,
  KeyPairCredentials,
  SnowflakeCredentialsType,
} from '../types';

type Props = {
  nextStep: () => void;
};

export function CredentialsStep({ nextStep }: Props) {
  const {
    credentials,
    setCredentials,
    testCredentials,
  } = useSnowflakeContext();

  const [passwordDisplay, setPasswordDisplay] = useState('');
  const [passwordManuallyChanged, setPasswordManuallyChanged] = useState(false);

  useEffect(() => {
    if (credentials?.authMethod === 'password') {
      const existingPwd = (credentials as PasswordCredentials)?.pwd;
      if (existingPwd && !passwordManuallyChanged) {
        setPasswordDisplay('********');
      } else {
        // Use the actual pwd from context if it was manually changed or if it's a new setup
        setPasswordDisplay(existingPwd ?? '');
      }
    } else {
      // Clear display if auth method is not password or no credentials
      setPasswordDisplay('');
      setPasswordManuallyChanged(false);
    }
  }, [
    credentials?.authMethod,
    (credentials as PasswordCredentials)?.pwd,
    passwordManuallyChanged,
  ]);

  const { mutate: test, isLoading: isTesting } = useMutation(testCredentials);

  const getCurrentAuthMethod = (): SnowflakeAuthMethod => {
    return credentials?.authMethod || 'password';
  };

  const handleAuthMethodChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newAuthMethod = event.target.value as SnowflakeAuthMethod;
    setCredentials((prev: SnowflakeCredentialsType | null) => {
      const base = {
        account: prev?.account ?? '',
        username: prev?.username ?? '',
        database: prev?.database ?? '',
        schema: prev?.schema ?? '',
        tableNames: prev?.tableNames ?? {},
        columnNames: prev?.columnNames,
      };
      if (newAuthMethod === 'keyPair') {
        const newCreds: KeyPairCredentials = {
          ...base,
          authMethod: 'keyPair',
          privateKey: '',
          privateKeyPass: undefined,
        };
        return newCreds;
      }
      const newCreds: PasswordCredentials = {
        ...base,
        authMethod: 'password',
        pwd: '',
      };
      return newCreds;
    });
  };

  const handleCommonFieldChange = (
    fieldName: 'account' | 'username' | 'database' | 'schema'
  ) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setCredentials((prev: SnowflakeCredentialsType | null) => {
      const currentAuth = prev?.authMethod || 'password';

      const commonFields = {
        account: prev?.account ?? '',
        username: prev?.username ?? '',
        database: prev?.database ?? '',
        schema: prev?.schema ?? '',
        tableNames: prev?.tableNames ?? {},
        columnNames: prev?.columnNames,
      };
      (commonFields as any)[fieldName] = value;

      if (currentAuth === 'keyPair') {
        const creds: KeyPairCredentials = {
          ...commonFields,
          authMethod: 'keyPair',
          privateKey: (prev as KeyPairCredentials)?.privateKey ?? '',
          privateKeyPass: (prev as KeyPairCredentials)?.privateKeyPass,
        };
        return creds;
      } // 'password'
      const creds: PasswordCredentials = {
        ...commonFields,
        authMethod: 'password',
        pwd: (prev as PasswordCredentials)?.pwd ?? '',
      };
      return creds;
    });
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setPasswordDisplay(value);
    setPasswordManuallyChanged(true);
    setCredentials((prev: SnowflakeCredentialsType | null) => {
      const creds: PasswordCredentials = {
        account: prev?.account ?? '',
        username: prev?.username ?? '',
        database: prev?.database ?? '',
        schema: prev?.schema ?? '',
        tableNames: prev?.tableNames ?? {},
        columnNames: prev?.columnNames,
        authMethod: 'password',
        pwd: value, // Update context with the real, typed password
      };
      return creds;
    });
  };

  const handlePrivateKeyChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    setCredentials((prev: SnowflakeCredentialsType | null) => {
      const creds: KeyPairCredentials = {
        account: prev?.account ?? '',
        username: prev?.username ?? '',
        database: prev?.database ?? '',
        schema: prev?.schema ?? '',
        tableNames: prev?.tableNames ?? {},
        columnNames: prev?.columnNames,
        authMethod: 'keyPair',
        privateKey: value,
        privateKeyPass: (prev as KeyPairCredentials)?.privateKeyPass,
      };
      return creds;
    });
  };

  const handlePrivateKeyPassChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setCredentials((prev: SnowflakeCredentialsType | null) => {
      const creds: KeyPairCredentials = {
        account: prev?.account ?? '',
        username: prev?.username ?? '',
        database: prev?.database ?? '',
        schema: prev?.schema ?? '',
        tableNames: prev?.tableNames ?? {},
        columnNames: prev?.columnNames,
        authMethod: 'keyPair',
        privateKey: (prev as KeyPairCredentials)?.privateKey ?? '',
        privateKeyPass: value || undefined,
      };
      return creds;
    });
  };

  const handleNextClick = async () => {
    const authMethod = getCurrentAuthMethod();
    let isValid = false;
    if (authMethod === 'password') {
      isValid = !!(
        credentials?.account &&
        credentials?.username &&
        (credentials as PasswordCredentials)?.pwd &&
        credentials?.database &&
        credentials?.schema
      );
    } else {
      // keyPair
      isValid = !!(
        credentials?.account &&
        credentials?.username &&
        (credentials as KeyPairCredentials)?.privateKey &&
        credentials?.database &&
        credentials?.schema
      );
    }

    if (!isValid) {
      Swal.fire({
        title: 'Invalid credentials',
        text:
          'Please fill all the required fields for the selected authentication method.',
        icon: 'error',
      });
      return;
    }

    test(null, {
      onSettled(state, error) {
        if (error || !state) {
          Swal.fire({
            title: 'Invalid credentials',
            text:
              'MadKudu is unable to connect with these credentials. Please check credentials or permissions for the selected authentication method.',
            icon: 'error',
          });
        } else {
          nextStep();
        }
      },
    });
  };

  return (
    <>
      <div className="row justify-content-center">
        <div className="col-8">
          <div className="card box-shadow-3d mb-3">
            <div className="card-header">
              <div className="d-flex align-items-center">
                <img
                  className="con-map-mk-integration-logo-size"
                  style={{ width: '30px' }}
                  src="/media/integrations/logos/snowflake-icon.png"
                />
                <h5 className="mb-0 ml-3">Snowflake</h5>
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-8">
                  <h5>Overview</h5>
                  <p className="mt-3">
                    To give MadKudu access to your Snowflake Database, you will
                    be invited go through the configuration process.
                  </p>
                </div>
                <div className="col-md-4">
                  <b className="d-block">Links</b>
                  <a
                    href="https://help.madkudu.com/docs/snowflake-1"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    How to connect Snowflake to MadKudu
                  </a>
                </div>
              </div>

              <h5 className="mt-3">Snowflake credentials</h5>

              <form>
                <div className="form-group row">
                  <label htmlFor="account" className="col-sm-2 col-form-label">
                    Account <sup className="text-danger">*</sup>
                  </label>
                  <div className="col-sm-10">
                    <input
                      type="text"
                      className="form-control"
                      id="account"
                      value={credentials?.account ?? ''}
                      required
                      onChange={handleCommonFieldChange('account')}
                    />
                  </div>
                </div>

                <div className="form-group row">
                  <label htmlFor="username" className="col-sm-2 col-form-label">
                    Username <sup className="text-danger">*</sup>
                  </label>
                  <div className="col-sm-10">
                    <input
                      type="text"
                      className="form-control"
                      id="username"
                      value={credentials?.username ?? ''}
                      required
                      onChange={handleCommonFieldChange('username')}
                    />
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-sm-2 col-form-label">
                    Auth Method <sup className="text-danger">*</sup>
                  </label>
                  <div className="col-sm-10">
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="authMethod"
                        id="authPassword"
                        value="password"
                        checked={getCurrentAuthMethod() === 'password'}
                        onChange={handleAuthMethodChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="authPassword"
                      >
                        Password
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="authMethod"
                        id="authKeyPair"
                        value="keyPair"
                        checked={getCurrentAuthMethod() === 'keyPair'}
                        onChange={handleAuthMethodChange}
                      />
                      <label className="form-check-label" htmlFor="authKeyPair">
                        Key Pair
                      </label>
                    </div>
                  </div>
                </div>

                {getCurrentAuthMethod() === 'password' && (
                  <div className="form-group row">
                    <label htmlFor="pwd" className="col-sm-2 col-form-label">
                      Password <sup className="text-danger">*</sup>
                    </label>
                    <div className="col-sm-10">
                      <input
                        type="password"
                        className="form-control"
                        id="pwd"
                        value={passwordDisplay}
                        required
                        onChange={handlePasswordChange}
                        onFocus={() => {
                          if (
                            passwordDisplay === '********' &&
                            !passwordManuallyChanged
                          ) {
                            setPasswordDisplay('');
                          }
                        }}
                        onBlur={() => {
                          // If field is empty after focus and blur, and password wasn't manually changed to be empty,
                          // and an original password exists, restore placeholder.
                          const originalPwdInContext = (credentials as PasswordCredentials)
                            ?.pwd;
                          if (
                            passwordDisplay === '' &&
                            originalPwdInContext &&
                            originalPwdInContext !== '' &&
                            !passwordManuallyChanged
                          ) {
                            // This case indicates user cleared the field but didn't intend to set an empty password
                            // for an existing configuration (unless they started typing and then cleared).
                            // If they manually typed and then cleared, passwordManuallyChanged would be true and display would be ''.
                            // This logic aims to restore placeholder if they just focused and blurred.
                            // However, if they cleared it, `handlePasswordChange` would have set context pwd to "".
                            // So we need to check context.
                            if (
                              (credentials as PasswordCredentials)?.pwd !== ''
                            ) {
                              setPasswordDisplay('********');
                            }
                          } else if (
                            passwordDisplay === '' &&
                            passwordManuallyChanged &&
                            (credentials as PasswordCredentials)?.pwd === ''
                          ) {
                            // User manually typed and then deleted to make it empty. Keep it empty.
                            // This case is fine.
                          } else if (
                            passwordDisplay !== '********' &&
                            !passwordManuallyChanged &&
                            originalPwdInContext
                          ) {
                            // If display somehow differs from placeholder but not manually changed (e.g. due to auth method switch)
                            // and original password exists, reset to placeholder.
                            // This might be too aggressive, relying on useEffect mostly.
                          }
                        }}
                      />
                    </div>
                  </div>
                )}

                {getCurrentAuthMethod() === 'keyPair' && (
                  <>
                    <div className="form-group row">
                      <label
                        htmlFor="privateKey"
                        className="col-sm-2 col-form-label"
                      >
                        Private Key <sup className="text-danger">*</sup>
                      </label>
                      <div className="col-sm-10">
                        <textarea
                          className="form-control"
                          id="privateKey"
                          rows={5}
                          value={
                            (credentials as KeyPairCredentials)?.privateKey ??
                            ''
                          }
                          required
                          onChange={handlePrivateKeyChange}
                          placeholder={`-----BEGIN PRIVATE KEY-----
...
-----END PRIVATE KEY-----`}
                        />
                        <small className="form-text text-muted">
                          Paste your PEM format private key.
                        </small>
                      </div>
                    </div>
                    <div className="form-group row">
                      <label
                        htmlFor="privateKeyPass"
                        className="col-sm-2 col-form-label"
                      >
                        Private Key Passphrase
                      </label>
                      <div className="col-sm-10">
                        <input
                          type="password"
                          className="form-control"
                          id="privateKeyPass"
                          value={
                            (credentials as KeyPairCredentials)
                              ?.privateKeyPass ?? ''
                          }
                          onChange={handlePrivateKeyPassChange}
                        />
                        <small className="form-text text-muted">
                          Optional: if your private key is encrypted.
                        </small>
                      </div>
                    </div>
                  </>
                )}

                <div className="form-group row">
                  <label htmlFor="database" className="col-sm-2 col-form-label">
                    Database <sup className="text-danger">*</sup>
                  </label>
                  <div className="col-sm-10">
                    <input
                      type="text"
                      className="form-control"
                      id="database"
                      value={credentials?.database ?? ''}
                      required
                      onChange={handleCommonFieldChange('database')}
                    />
                  </div>
                </div>

                <div className="form-group row">
                  <label htmlFor="schema" className="col-sm-2 col-form-label">
                    Schema <sup className="text-danger">*</sup>
                  </label>
                  <div className="col-sm-10">
                    <input
                      type="text"
                      className="form-control"
                      id="schema"
                      value={credentials?.schema ?? ''}
                      required
                      onChange={handleCommonFieldChange('schema')}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 pb-4 text-center">
        <button
          className="btn btn-primary"
          disabled={isTesting}
          onClick={handleNextClick}
        >
          {isTesting ? 'Testing credentials...' : 'Next'}
        </button>
      </div>
    </>
  );
}
